import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../../redux/store";
import { ApiError } from "./types/common-types";
import {
  GamecardProviderState,
  GamecardProviderResponse,
  GamecardProvider,
} from "./types/game-types";
import { PaginationReturn } from "./types/types";

const initialState: GamecardProviderState = {
  gamecardProviders: [],
  allGamecardProviders: [],
  gamecardProvider: null,
  selectedGamecardProvider: null,
  loading: true,
  allLoading: false,
  error: null,
  allError: null,
  success: false,
  total: 0,
  perPage: 15,
  currentPage: 1,
  lastPage: 1,
  pagination: {
    nextPageUrl: null,
    prevPageUrl: null,
  },
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// CREATE DataProvider
export const createGamecardProvider = createAsyncThunk<
  GamecardProviderResponse,
  FormData | Omit<GamecardProvider, "id" | "created_at" | "updated_at">,
  { rejectValue: ApiError; state: RootState }
>("gamecardProviders/create", async (gamecardProviderData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = gamecardProviderData instanceof FormData;
    const res = await axios.post(
      `${API_URL}/game-cards-providers`,
      gamecardProviderData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      },
    );
    return {
      message: res.data.message,
      gamecardProvider: res.data.data,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create game card provider" },
    );
  }
});

// READ (fetch all) GamecardProviders
export const fetchGamecardProviders = createAsyncThunk<
  GamecardProviderResponse & PaginationReturn,
  void | { url: string } | any,
  { rejectValue: ApiError; state: RootState }
>("gamecardProviders/fetchAll", async (urlFilters, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const url: string | undefined = urlFilters?.url;
    const apiUrl = url || `${API_URL}/game-cards-providers`;
    if (urlFilters?.url) {
      urlFilters.url = "";
    }
    const res = await axios.get(apiUrl, {
      params: urlFilters,
      headers: { Authorization: `Bearer ${token}` },
    });
    return {
      message: res.data.data.message,
      gamecardProviders: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
      prev_page_url: res.data.data.prev_page_url,
      next_page_url: res.data.data.next_page_url,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch game card providers" },
    );
  }
});

// READ (fetch all providers without pagination)
export const fetchAllGamecardProviders = createAsyncThunk<
  GamecardProvider[],
  void,
  { rejectValue: ApiError; state: RootState }
>("gamecardProviders/fetchAllList", async (_, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const url = new URL(`${API_URL}/game-cards-providers`);
    url.searchParams.set("per_page", "1000");
    const res = await axios.get(url.toString(), {
      headers: { Authorization: `Bearer ${token}` },
    });
    const payload = res.data?.data;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch game card providers" },
    );
  }
});

// READ (fetch one) DataProvider by ID
export const fetchGamecardProviderById = createAsyncThunk<
  GamecardProvider,
  number,
  { rejectValue: ApiError; state: RootState }
>("gamecardProviders/fetchById", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/game-cards-providers/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch game card provider" },
    );
  }
});

// UPDATE DataProvider
export const updateGamecardProvider = createAsyncThunk<
  GamecardProvider,
  { id: number; data: FormData | Partial<GamecardProvider> },
  { rejectValue: ApiError; state: RootState }
>("gamecardProviders/update", async ({ id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = data instanceof FormData;
    const res = await axios.post(
      `${API_URL}/game-cards-providers/${id}?_method=PUT`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      },
    );
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to update game card provider" },
    );
  }
});

// DELETE DataProvider
export const deleteGamecardProvider = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("gamecardProviders/delete", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/game-cards-providers/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to delete game card provider" },
    );
  }
});

// Slice
const gamecardProviderSlice = createSlice({
  name: "gamecardProviders",
  initialState,
  reducers: {
    resetGamecardProviderState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.selectedGamecardProvider = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createGamecardProvider.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createGamecardProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.gamecardProvider) {
          state.gamecardProviders.unshift(action.payload.gamecardProvider);
        }
      })
      .addCase(createGamecardProvider.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to create game card provider";
      })

      // Fetch All
      .addCase(fetchGamecardProviders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGamecardProviders.fulfilled, (state, action) => {
        state.loading = false;
        state.gamecardProviders = action.payload.gamecardProviders || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 15;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
        state.pagination.nextPageUrl = action.payload.next_page_url;
        state.pagination.prevPageUrl = action.payload.prev_page_url;
      })
      .addCase(fetchGamecardProviders.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch data providers";
      })
      .addCase(fetchAllGamecardProviders.pending, (state) => {
        state.allLoading = true;
        state.allError = null;
      })
      .addCase(fetchAllGamecardProviders.fulfilled, (state, action) => {
        state.allLoading = false;
        state.allGamecardProviders = action.payload || [];
      })
      .addCase(fetchAllGamecardProviders.rejected, (state, action) => {
        state.allLoading = false;
        state.allError =
          action.payload?.message || "Failed to fetch game card providers";
      })

      // Fetch One
      .addCase(fetchGamecardProviderById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedGamecardProvider = null;
      })
      .addCase(fetchGamecardProviderById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedGamecardProvider = action.payload;
      })
      .addCase(fetchGamecardProviderById.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch game card provider";
      })

      // Update
      .addCase(updateGamecardProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateGamecardProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.gamecardProviders = state.gamecardProviders.map((provider) =>
          provider.id === action.payload.id ? action.payload : provider,
        );
        if (state.selectedGamecardProvider?.id === action.payload.id) {
          state.selectedGamecardProvider = action.payload;
        }
      })
      .addCase(updateGamecardProvider.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to update game card provider";
      })

      // Delete
      .addCase(deleteGamecardProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteGamecardProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.gamecardProviders = state.gamecardProviders.filter(
          (provider) => provider.id !== action.payload,
        );
      })
      .addCase(deleteGamecardProvider.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to delete game card provider";
      });
  },
});

export const { resetGamecardProviderState } = gamecardProviderSlice.actions;
export default gamecardProviderSlice.reducer;
